"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Whatevermarksuppliesoems() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const pathways = [{
    number: "01",
    kanji: "標",
    title: "Industrial Labeling",
    description: "Precision-engineered identification solutions for complex electrical environments, ensuring compliance and safety across all facility infrastructure.",
    details: ["UL 508A compliant materials", "High-durability phenolic", "Rapid turnaround production"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_bench.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_bench.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_bench.png-wvc-sizes",
    alt: "Precision industrial labeling equipment in a workshop",
    to: "/ProductsPage"
  }, {
    number: "02",
    kanji: "刻",
    title: "Laser Engraving",
    description: "Advanced laser marking services for metal and synthetic components, providing permanent, high-contrast identification for critical industrial parts.",
    details: ["Direct Part Marking (DPM)", "Stainless steel and aluminum", "Custom logo and serializing"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png-wvc-sizes",
    alt: "Laser engraving machine marking a metal component",
    to: "/CustomEngravingPage"
  }, {
    number: "03",
    kanji: "電",
    title: "Arc Flash Solutions",
    description: "Comprehensive arc flash labeling packages designed to meet NFPA 70E standards, protecting personnel and ensuring regulatory compliance.",
    details: ["Custom hazard analysis data", "UV-resistant outdoor labels", "Full submittal documentation"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_compliance.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_compliance.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_compliance.png-wvc-sizes",
    alt: "Arc flash safety label applied to electrical equipment",
    to: "/ArcFlashLabelsPage"
  }];
  return <section data-section-id="3591" id="whatevermarksuppliesoems" className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] mb-6" style={{
          color: "var(--chart-3)"
        }}>
            05 — Our Core Capabilities
          </p>
          <h2 className="font-sans font-default text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight">
            Precision marking for industry.
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground max-w-[580px] mx-auto leading-relaxed">
            From high-durability phenolic tags to advanced laser-marked metal components, we provide the identification infrastructure that keeps your operations compliant, safe, and efficient.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {pathways.map((pathway, i) => {
          return <PathwayCard key={i} pathway={pathway} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PathwayCard({
  pathway,
  index
}: {
  pathway: {
    number: string;
    kanji: string;
    title: string;
    description: string;
    details: string[];
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    to: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} data-index={index} className={`group relative flex flex-col border border-border bg-card transition-all duration-700 ease-out hover:border-[var(--chart-3)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <div className="flex flex-col h-full p-8 md:p-10">
        {}
        <div className="flex items-baseline justify-between mb-8">
          <span className="font-sans font-default text-6xl md:text-7xl text-foreground leading-none">
            {pathway.number}
          </span>
          <span className="font-sans text-4xl md:text-5xl leading-none" style={{
          color: "var(--chart-3)"
        }}>
            {pathway.kanji}
          </span>
        </div>

        {}
        <h3 className="font-sans font-default text-2xl md:text-3xl text-foreground mb-4 tracking-tight">
          {pathway.title}
        </h3>

        {}
        <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-8">
          {pathway.description}
        </p>

        {}
        <ul className="space-y-2 mb-10">
          {pathway.details.map((detail, di) => <li data-index={di} key={di} className="font-mono text-xs uppercase tracking-wider text-foreground/80 flex items-start gap-3">
              <span className="text-muted-foreground" aria-hidden="true">—</span>
              <span>{detail}</span>
            </li>)}
        </ul>

        {}
        <div className="relative overflow-hidden mb-8 mt-auto">
          <img src={pathway.image} data-wvc-srcset={pathway.srcset} data-wvc-sizes={pathway.sizes} alt={pathway.alt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        </div>

        {}
        <Button asChild variant="ghost" className="w-full justify-between px-0 hover:bg-transparent text-foreground hover:text-[var(--chart-3)] border-t border-border rounded-md pt-6">
          <Link to={pathway.to}>
            <span className="font-mono text-sm uppercase tracking-[0.2em]">Learn More</span>
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Button>
      </div>
    </div>;
}